

// @mui material components
import { createTheme } from "@mui/material";

// Base styles
import colors from "assets/theme/base/colors";
import breakpoints from "assets/theme/base/breakpoints";
import typography from "assets/theme/base/typography";
import boxShadows from "assets/theme/base/boxShadows";
import borders from "assets/theme/base/borders";
import spacing from "assets/theme/base/spacing";
import width from "assets/theme/base/width";
import type { Types as WidthTypes } from "assets/theme/base/width";
import globals from "assets/theme/base/globals";
import { getHmoColors } from "assets/theme/hmoColors";
import { getHmoTypography } from "assets/theme/hmoTypography";
import { getHmoSpacing } from "assets/theme/hmoSpacing";
import { getHmoBoxShadows } from "assets/theme/hmoBoxShadows";

// Helper Functions
import boxShadow from "assets/theme/functions/boxShadow";
import hexToRgb from "assets/theme/functions/hexToRgb";
import linearGradient from "assets/theme/functions/linearGradient";
import pxToRem from "assets/theme/functions/pxToRem";
import rgba from "assets/theme/functions/rgba";

// base styles for @mui material components
import sidenav from "assets/theme/components/sidenav";
import list from "assets/theme/components/list";
import listItem from "assets/theme/components/list/listItem";
import listItemText from "assets/theme/components/list/listItemText";
import card from "assets/theme/components/card";
import cardMedia from "assets/theme/components/card/cardMedia";
import cardContent from "assets/theme/components/card/cardContent";
import button from "assets/theme/components/button";
import iconButton from "assets/theme/components/iconButton";
import input from "assets/theme/components/form/input";
import inputLabel from "assets/theme/components/form/inputLabel";
import inputOutlined from "assets/theme/components/form/inputOutlined";
import textField from "assets/theme/components/form/textField";
import menu from "assets/theme/components/menu";
import menuItem from "assets/theme/components/menu/menuItem";
import switchButton from "assets/theme/components/form/switchButton";
import tableContainer from "assets/theme/components/table/tableContainer";
import tableHead from "assets/theme/components/table/tableHead";
import tableCell from "assets/theme/components/table/tableCell";
import linearProgress from "assets/theme/components/linearProgress";
import breadcrumbs from "assets/theme/components/breadcrumbs";
import slider from "assets/theme/components/slider";
import tooltip from "assets/theme/components/tooltip";
import appBar from "assets/theme/components/appBar";
import tabs from "assets/theme/components/tabs";
import tab from "assets/theme/components/tabs/tab";
import select from "assets/theme/components/form/select";
import formControlLabel from "assets/theme/components/form/formControlLabel";
import formLabel from "assets/theme/components/form/formLabel";
import checkbox from "assets/theme/components/form/checkbox";
import radio from "assets/theme/components/form/radio";
import container from "assets/theme/components/container";
import popover from "assets/theme/components/popover";
import buttonBase from "assets/theme/components/buttonBase";
import icon from "assets/theme/components/icon";
import svgIcon from "assets/theme/components/svgIcon";
import link from "assets/theme/components/link";
import dialog from "assets/theme/components/dialog";
import dialogTitle from "assets/theme/components/dialog/dialogTitle";
import dialogContent from "assets/theme/components/dialog/dialogContent";
import dialogContentText from "assets/theme/components/dialog/dialogContentText";
import dialogActions from "assets/theme/components/dialog/dialogActions";


// Default theme (backward compatibility)
// Note: We don't pass spacing to createTheme - MUI will create its own spacing function
// We attach our custom spacing properties AFTER createTheme returns
const defaultTheme = createTheme({
  breakpoints: { ...breakpoints },
  width: { ...width },
  palette: { ...colors } as any, // Type assertion needed due to custom properties
  typography: { ...typography },
  boxShadows: { ...boxShadows },
  borders: { ...borders },
  functions: {
    boxShadow,
    hexToRgb,
    linearGradient,
    pxToRem,
    rgba,
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ...globals,
        ...container,
      },
    },
    MuiDrawer: { ...sidenav },
    MuiList: { ...list },
    MuiListItem: { ...listItem },
    MuiListItemText: { ...listItemText },
    MuiCard: { ...card },
    MuiCardMedia: { ...cardMedia },
    MuiCardContent: { ...cardContent },
    MuiButton: { ...button },
    MuiIconButton: { ...iconButton },
    MuiInput: { ...input },
    MuiInputLabel: { ...inputLabel },
    MuiOutlinedInput: { ...inputOutlined },
    MuiTextField: { ...textField },
    MuiMenu: { ...menu },
    MuiMenuItem: { ...menuItem },
    MuiSwitch: { ...switchButton },
    MuiTableContainer: { ...tableContainer },
    MuiTableHead: { ...tableHead },
    MuiTableCell: { ...tableCell },
    MuiLinearProgress: { ...linearProgress },
    MuiBreadcrumbs: { ...breadcrumbs },
    MuiSlider: { ...slider },
    MuiTooltip: { ...tooltip },
    MuiAppBar: { ...appBar },
    MuiTabs: { ...tabs },
    MuiTab: { ...tab },
    MuiSelect: { ...select },
    MuiFormControlLabel: { ...formControlLabel },
    MuiFormLabel: { ...formLabel },
    MuiCheckbox: { ...checkbox },
    MuiRadio: { ...radio },
    MuiPopover: { ...popover },
    MuiButtonBase: { ...buttonBase },
    MuiIcon: { ...icon },
    MuiSvgIcon: { ...svgIcon },
    MuiLink: { ...link },
    MuiDialog: { ...dialog },
    MuiDialogTitle: { ...dialogTitle },
    MuiDialogContent: { ...dialogContent },
    MuiDialogContentText: { ...dialogContentText },
    MuiDialogActions: { ...dialogActions },
  },
});

// Attach custom spacing properties to the theme's spacing function after createTheme
// This preserves MUI's default spacing function while adding our custom properties
Object.assign(defaultTheme.spacing, spacing);


export default defaultTheme;

import { HMOType } from "constants/hmo";

/**
 * Factory function to create theme with HMO colors
 * @param hmo - HMO identifier or null for default
 * @param direction - Text direction 'ltr' or 'rtl', defaults to 'ltr'
 * @returns MUI theme with HMO-specific colors merged
 */
export function createThemeWithHmo(hmo?: HMOType, direction: 'ltr' | 'rtl' = 'ltr') {
  const hmoColors = getHmoColors(hmo || null);
  const hmoTypography = getHmoTypography(hmo || null);
  const hmoSpacing = getHmoSpacing(hmo || null);
  const hmoBoxShadows = getHmoBoxShadows(hmo || null);
  
  const { scrollbar, ...paletteBaseColors } = colors;
  
  // Deep merge colors: start with base colors, then apply HMO overrides
  const mergedColors = {
    ...paletteBaseColors,
    ...hmoColors, // Spread all base colors first (white, text, transparent, etc.)
    primary: {
      ...colors.primary,
      ...(hmoColors.primary || {}),
    },
    secondary: {
      ...colors.secondary,
      ...(hmoColors.secondary || {}),
    },
    gradients: {
      ...colors.gradients,
      ...(hmoColors.gradients || {}),
    },
    coloredShadows: {
      ...colors.coloredShadows,
      ...(hmoColors.coloredShadows || {}),
    },
    // overlay is already in paletteBaseColors, but ensure it's present
    overlay: colors.overlay,
  };

  // Merge typography with HMO overrides
  const mergedTypography = {
    ...typography,
    ...hmoTypography,
  };

  // Merge spacing with HMO overrides
  const mergedSpacingValues = {
    ...spacing,
    ...hmoSpacing,
  };

  // Merge box shadows with HMO overrides
  const mergedBoxShadows = {
    ...boxShadows,
    ...hmoBoxShadows,
  };

  // Note: We don't pass spacing to createTheme - MUI will create its own spacing function
  // We attach our custom spacing properties AFTER createTheme returns
  const theme = createTheme({
    direction,
    breakpoints: { ...breakpoints },
    width: { ...width },
    palette: { ...mergedColors } as any, // Type assertion needed due to custom properties
    typography: mergedTypography as any, // Preserve custom size property
    boxShadows: mergedBoxShadows,
    borders: { ...borders },
    functions: {
      boxShadow,
      hexToRgb,
      linearGradient,
      pxToRem,
      rgba,
    },

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ...globals,
          ...container,
        },
      },
      MuiDrawer: { ...sidenav },
      MuiList: { ...list },
      MuiListItem: { ...listItem },
      MuiListItemText: { ...listItemText },
      MuiCard: { ...card },
      MuiCardMedia: { ...cardMedia },
      MuiCardContent: { ...cardContent },
      MuiButton: { ...button },
      MuiIconButton: { ...iconButton },
      MuiInput: { ...input },
      MuiInputLabel: { ...inputLabel },
      MuiOutlinedInput: { ...inputOutlined },
      MuiTextField: { ...textField },
      MuiMenu: { ...menu },
      MuiMenuItem: { ...menuItem },
      MuiSwitch: { ...switchButton },
      MuiTableContainer: { ...tableContainer },
      MuiTableHead: { ...tableHead },
      MuiTableCell: { ...tableCell },
      MuiLinearProgress: { ...linearProgress },
      MuiBreadcrumbs: { ...breadcrumbs },
      MuiSlider: { ...slider },
      MuiTooltip: { ...tooltip },
      MuiAppBar: { ...appBar },
      MuiTabs: { ...tabs },
      MuiTab: { ...tab },
      MuiSelect: { ...select },
      MuiFormControlLabel: { ...formControlLabel },
      MuiFormLabel: { ...formLabel },
      MuiCheckbox: { ...checkbox },
      MuiRadio: { ...radio },
      MuiPopover: { ...popover },
      MuiButtonBase: { ...buttonBase },
      MuiIcon: { ...icon },
      MuiSvgIcon: { ...svgIcon },
      MuiLink: { ...link },
      MuiDialog: { ...dialog },
      MuiDialogTitle: { ...dialogTitle },
      MuiDialogContent: { ...dialogContent },
      MuiDialogContentText: { ...dialogContentText },
      MuiDialogActions: { ...dialogActions },
    },
  });

  // Attach custom spacing properties to the theme's spacing function after createTheme
  // This preserves MUI's default spacing function while adding our custom properties
  Object.assign(theme.spacing, mergedSpacingValues);

  return theme;
}
